import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Download, Loader2, Plug } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";

interface ConnectorStatus {
  leadKeyPresent: boolean;
  publicId: string | null;
  wordpressConfigured: boolean;
  siteAgentActive?: boolean;
  lastLeadFromPlugin?: { at: string; source: string; isTest: boolean } | null;
}

const STEPS = [
  "In WordPress, open Plugins, Add New Plugin, Upload Plugin and choose the file you just downloaded.",
  "Click Install Now, then Activate.",
  "Open Settings, DrPR Connector and press Test connection.",
];

function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (!Number.isFinite(mins)) return "";
  if (mins < 2) return "just now";
  if (mins < 120) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} hours ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function Status({ ok, children, testId }: { ok: boolean; children: ReactNode; testId: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${ok ? "text-emerald-400" : "text-amber-400"}`} data-testid={testId} data-ok={ok}>
      {ok ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
      {children}
    </span>
  );
}

/**
 * Settings, Publishing: download the "DrPR Connector" WordPress plugin with this workspace's lead key and chat id already inside.
 * Owner / admin only (the caller decides; the routes enforce it too). GET /api/workspaces/:id/wp-connector.zip and .../wp-connector/status.
 */
export function WpConnectorDownload() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { data, isLoading, isError } = useQuery<ConnectorStatus>({
    queryKey: ["/api/workspaces", wsId, "wp-connector", "status"],
    enabled: !!wsId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/wp-connector/status`, { credentials: "include" });
      if (!res.ok) throw new Error(`status ${res.status}`);
      return res.json();
    },
  });
  if (!wsId) return null;

  const last = data?.lastLeadFromPlugin;
  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="section-wp-connector">
      <div className="flex items-center gap-3">
        <Plug size={20} className="text-[#21759B]" />
        <h2 className="font-display font-bold text-foreground">WordPress connector</h2>
      </div>
      <p className="text-xs text-muted-foreground/70" data-testid="text-wp-connector-description">
        Install one small plugin on your WordPress site and you are connected: leads from your website forms reach your Lead Desk, publishing keeps working on hosts that block sign-in, and your DrPR chat appears on the site. Your workspace details are already inside the file, so there is nothing to copy and paste.
      </p>

      <a
        href={`/api/workspaces/${wsId}/wp-connector.zip`}
        download="drpr-connector.zip"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-[#21759B]/20 text-[#21759B] border border-[#21759B]/30 hover:bg-[#21759B]/30 transition-all"
        data-testid="link-download-wp-connector"
      >
        <Download size={12} /> Download plugin
      </a>

      <ol className="list-decimal pl-5 space-y-1 text-xs text-muted-foreground" data-testid="list-wp-connector-steps">
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]" data-testid="text-wp-connector-status">
        {isLoading && (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Checking…</span>
        )}
        {isError && !data && <span className="text-muted-foreground">Status unavailable right now. The download still works.</span>}
        {data && (
          <>
            <Status ok={data.leadKeyPresent} testId="status-wp-lead-key">
              {data.leadKeyPresent ? "Lead key ready" : "Lead key is created when you download"}
            </Status>
            <Status ok={data.wordpressConfigured} testId="status-wp-publishing">
              {data.wordpressConfigured ? "WordPress publishing connected" : "WordPress publishing not connected above"}
            </Status>
            {data.siteAgentActive === false && (
              <Status ok={false} testId="status-wp-chat">Chat shows once your Site Agent is switched on</Status>
            )}
            <span className="text-muted-foreground" data-testid="status-wp-last-lead">
              {last ? `${last.isTest ? "Last test lead" : "Last lead from your site"}: ${timeAgo(last.at)}` : "No leads from the plugin yet"}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
